import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  useTheme,
  Alert,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Phone,
  Person,
  AppRegistration,
  Login as LoginIcon,
} from "@mui/icons-material";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { useAuth } from "../../../utils/AuthContext";

// Images
import KaasLogo from "../Images/Vetrina _Logo.png";
import AuthBg from "../Images/b2efdca3-f62c-4ee8-91ea-c5710a5d4dc8.jpg";

const Log = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [tabIndex, setTabIndex] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    username: "",
    mobile_number: "",
    otp: "",
  });

  const [showOtpField, setShowOtpField] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const API_URL = "https://tdtlworld.com/dss-kaas-backend/";

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOTP = async () => {
    if (!formData.email) {
      setError("Please enter your email address first.");
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API_URL}auth/verify-email/`, {
        email: formData.email,
      });
      Swal.fire({
        icon: "success",
        title: "OTP Sent",
        text: "Please check your email for the OTP.",
        timer: 2000,
        showConfirmButton: false,
      });
      setShowOtpField(true);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to send OTP. Please try again.",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_URL}auth/login/`, {
        email: formData.email,
        password: formData.password,
      });

      const { access, role, email } = response.data;

      // Use AuthContext login to maintain global state
      auth.login(access, role, email);

      Swal.fire({
        icon: "success",
        title: "Login Successful",
        text: `Welcome back, ${role}!`,
        timer: 1500,
        showConfirmButton: false,
      });

      // Role-based redirection
      if (role === "Admin") {
        navigate("/db-shiksha/kaas/admin/dashboard");
      } else if (role === "Instructor") {
        navigate("/db-shiksha/kaas/admin/dashboard");
      } else {
        navigate("/db-shiksha/kaas/student/home");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Invalid credentials. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_URL}auth/signup/`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        mobile_number: formData.mobile_number,
        username: formData.username,
        password: formData.password,
        otp: formData.otp,
      });
      Swal.fire({
        icon: "success",
        title: "Registration Successful",
        text: "You can now log in with your credentials.",
        timer: 2000,
        showConfirmButton: false,
      });
      setTabIndex(0);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Registration failed. Please check your details.",
      );
    } finally {
      setLoading(false);
    }
  };

  const commonTextFieldProps = {
    fullWidth: true,
    variant: "outlined",
    margin: "normal",
    required: true,
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 6,
            background: "linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: "center", zIndex: 1 }}
          >
            <img
              src={KaasLogo}
              alt="Kaas Logo"
              style={{
                height: "80px",
                marginBottom: "40px",
                filter: "brightness(0) invert(1)",
              }}
            />
            <Typography variant="h3" fontWeight={800} gutterBottom>
              Elevate Your{" "}
              <Box component="span" sx={{ color: "#D4AF37" }}>
                Potential
              </Box>
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.7, mb: 4 }}>
              Join thousands of learners mastering new technologies everyday.
            </Typography>
            <Box
              component="img"
              src={AuthBg}
              sx={{
                width: "100%",
                maxWidth: "500px",
                borderRadius: "24px",
                boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
                border: "1px solid rgba(212, 175, 55, 0.2)",
              }}
            />
          </motion.div>
          <Box
            sx={{
              position: "absolute",
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              bgcolor: "rgba(212, 175, 55, 0.05)",
              borderRadius: "50%",
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 5 },
                borderRadius: "32px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "text.primary",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <img
                  src={KaasLogo}
                  alt="Kaas Logo"
                  style={{
                    height: "40px",
                    marginBottom: "20px",
                    filter: isDark ? "brightness(0) invert(1)" : "none",
                  }}
                />
                <Typography variant="h4" fontWeight={800} color="text.primary">
                  {tabIndex === 0 ? "Welcome Back" : "Get Started"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tabIndex === 0
                    ? "Enter your credentials to access your account"
                    : "Create an account to start your learning journey"}
                </Typography>
              </Box>

              <Tabs
                value={tabIndex}
                onChange={(e, v) => setTabIndex(v)}
                centered
                sx={{
                  mb: 4,
                  "& .MuiTabs-indicator": { height: 3, borderRadius: "3px" },
                }}
              >
                <Tab
                  label="Login"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "text.secondary",
                    "&.Mui-selected": { color: "#D4AF37" },
                  }}
                />
                <Tab
                  label="Register"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "text.secondary",
                    "&.Mui-selected": { color: "#D4AF37" },
                  }}
                />
              </Tabs>

              <AnimatePresence mode="wait">
                <motion.div
                  key={tabIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {error && (
                    <Alert
                      severity="error"
                      sx={{ mb: 3, borderRadius: "12px" }}
                    >
                      {error}
                    </Alert>
                  )}

                  <form
                    onSubmit={tabIndex === 0 ? handleLogin : handleRegister}
                  >
                    {tabIndex === 1 && (
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              {...commonTextFieldProps}
                              label="First Name"
                              name="first_name"
                              value={formData.first_name}
                              onChange={handleInputChange}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              {...commonTextFieldProps}
                              label="Last Name"
                              name="last_name"
                              value={formData.last_name}
                              onChange={handleInputChange}
                            />
                          </Grid>
                        </Grid>
                        <TextField
                          {...commonTextFieldProps}
                          label="Username"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person sx={{ color: "#D4AF37" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          {...commonTextFieldProps}
                          label="Mobile Number"
                          name="mobile_number"
                          type="number"
                          value={formData.mobile_number}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone sx={{ color: "#D4AF37" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        {...commonTextFieldProps}
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        sx={{ flex: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: "#D4AF37" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {tabIndex === 1 && !showOtpField && (
                        <Button
                          variant="outlined"
                          onClick={handleSendOTP}
                          disabled={otpLoading}
                          sx={{ 
                            height: '56px', 
                            mt: '8px', 
                            borderRadius: '12px',
                            borderColor: '#D4AF37',
                            color: '#D4AF37',
                            '&:hover': {
                              borderColor: '#B8529E',
                              color: '#B8529E',
                            }
                          }}
                        >
                          {otpLoading ? "..." : "Verify"}
                        </Button>
                      )}
                    </Box>

                    {showOtpField && tabIndex === 1 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                      >
                        <TextField
                          {...commonTextFieldProps}
                          label="Enter OTP"
                          name="otp"
                          value={formData.otp}
                          onChange={handleInputChange}
                          autoFocus
                          placeholder="Check your email"
                        />
                      </motion.div>
                    )}

                    <TextField
                      {...commonTextFieldProps}
                      label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: "#D4AF37" }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {tabIndex === 0 && (
                      <Box sx={{ textAlign: "right", mb: 1 }}>
                        <Link
                          to="/db-shiksha/kaas/forgotPassword"
                          style={{
                            textDecoration: "none",
                            color: "#D4AF37",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </Box>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      type="submit"
                      disabled={loading}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        borderRadius: "50px",
                        fontWeight: 800,
                        fontSize: "1.1rem",
                      }}
                      startIcon={
                        tabIndex === 0 ? <LoginIcon /> : <AppRegistration />
                      }
                    >
                      {loading
                        ? "Processing..."
                        : tabIndex === 0
                          ? "Log In"
                          : "Register"}
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ mt: 2 }}
                    >
                      By continuing, you agree to our{" "}
                      <Link to="/terms" style={{ color: "#D4AF37" }}>
                        Terms
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" style={{ color: "#D4AF37" }}>
                        Privacy Policy
                      </Link>
                    </Typography>
                  </form>
                </motion.div>
              </AnimatePresence>
            </Paper>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Log;
